import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { renderGoogleButton } from "../lib/google";

export default function Login() {
  const { user, loading, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const btnRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [selectedRole, setSelectedRole] = useState<"talent" | "company">("talent");
  const selectedRoleRef = useRef(selectedRole);
  selectedRoleRef.current = selectedRole;

  useEffect(() => {
    if (!loading && user) {
      navigate(user.onboarded ? (user.role === "company" ? "/company" : "/app") : "/onboarding", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!btnRef.current) return;
    const ok = renderGoogleButton(btnRef.current, async (idToken) => {
      setError(null);
      setSigningIn(true);
      try {
        const loggedIn = await loginWithGoogle(idToken, selectedRoleRef.current);
        navigate(loggedIn.onboarded ? (loggedIn.role === "company" ? "/company" : "/app") : "/onboarding");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Login failed. Please try again.");
      } finally {
        setSigningIn(false);
      }
    });
    if (!ok) {
      setError("Google login requires VITE_GOOGLE_CLIENT_ID. Please check your frontend/.env file.");
    }
  }, [loginWithGoogle, navigate]);

  return (
    <div className="flex min-h-screen">
      {/* Left branding panel — hidden on mobile, shown on md+ */}
      <div className="hidden md:flex md:w-[420px] lg:w-[480px] flex-col justify-between bg-navy p-8 lg:p-12 text-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-teal/10 blur-3xl" />
        <div className="absolute -left-10 bottom-20 h-48 w-48 rounded-full bg-coral/10 blur-3xl" />

        {/* Top: Logo */}
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="personaw" className="h-8 w-8 rounded-lg object-contain" />
            <span className="text-lg font-bold tracking-tight">personaw</span>
          </Link>
        </div>

        {/* Center: Value prop */}
        <div className="relative z-10 space-y-6">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm">
            <p className="text-sm leading-relaxed text-white/90 font-medium italic">
              "In the AI era, go beyond resumes. Discover opportunities where your Work style and Team chemistry are truly verified."
            </p>
            <div className="mt-5 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-teal to-coral text-sm font-bold">
                W
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Work & We</p>
                <p className="text-xs text-white/50">AI Career Platform</p>
              </div>
            </div>
          </div>

          {/* Feature highlights */}
          <div className="space-y-3">
            {[
              { icon: "✦", text: "AI Adaptive Resume & Cover Letter" },
              { icon: "◈", text: "Team-fit Matching Engine" },
              { icon: "◉", text: "AI Chat Interview & Dual Report" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3 text-white/70">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/10 text-xs text-teal-light">
                  {item.icon}
                </span>
                <span className="text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom: copyright */}
        <div className="relative z-10">
          <p className="text-[11px] text-white/30">© 2025 personaw.com · All rights reserved.</p>
        </div>
      </div>

      {/* Right side — login form */}
      <div className="flex flex-1 flex-col">
        {/* Mobile-only top bar */}
        <header className="flex items-center justify-between px-5 py-4 md:hidden">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="personaw" className="h-6 w-6 rounded-md object-contain" />
            <span className="text-[15px] font-bold tracking-tight text-navy">personaw</span>
          </Link>
          <Link to="/pricing" className="text-xs font-medium text-slate-500 hover:text-navy">
            Pricing
          </Link>
        </header>

        {/* PC-only top-right nav */}
        <div className="hidden md:flex items-center justify-end gap-4 px-8 py-5 text-sm text-slate-500">
          <Link to="/" className="hover:text-navy transition">Home</Link>
          <Link to="/pricing" className="hover:text-navy transition">Pricing</Link>
        </div>

        {/* Center content */}
        <div className="flex flex-1 flex-col items-center justify-center px-6 pb-16 sm:px-12">
          <div className="w-full max-w-sm">
            {/* Logo for login section */}
            <div className="flex flex-col items-center text-center mb-8">
              <img
                src="/logo.png"
                alt="personaw logo"
                className="h-14 w-14 rounded-2xl object-contain shadow-sm md:h-16 md:w-16"
              />
              <h1 className="mt-5 text-xl font-bold text-navy sm:text-2xl">Welcome to personaw</h1>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                {selectedRole === "talent"
                  ? "Build your AI Persona and verify your Team-fit."
                  : "Register your team culture and hire top fit talents."}
              </p>
            </div>

            {/* Role Selection Tabs */}
            <div className="mb-6 rounded-xl bg-slate-100 p-1 flex items-center border border-slate-200">
              <button
                type="button"
                onClick={() => setSelectedRole("talent")}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                  selectedRole === "talent"
                    ? "bg-white text-navy shadow-sm border border-slate-200/60"
                    : "text-slate-500 hover:text-navy"
                }`}
              >
                <span>👤</span> Job Seeker (구직자)
              </button>
              <button
                type="button"
                onClick={() => setSelectedRole("company")}
                className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                  selectedRole === "company"
                    ? "bg-white text-navy shadow-sm border border-slate-200/60"
                    : "text-slate-500 hover:text-navy"
                }`}
              >
                <span>🏢</span> HR & Company (기업)
              </button>
            </div>

            {/* Google button area */}
            <div className="flex justify-center" ref={btnRef} />
            {signingIn && <p className="mt-3 text-center text-xs text-slate-400">Signing in...</p>}
            {error && <p className="mt-3 text-center max-w-[320px] mx-auto text-xs text-coral">{error}</p>}

            {/* Divider */}
            <div className="my-8 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[11px] text-slate-400">Key Features</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            {/* Feature list */}
            <ul className="space-y-3 text-sm text-slate-600">
              {[
                "AI-powered resume & cover letter generation",
                "Team-fit matching for the perfect team connection",
                "AI chat interview & dual-sided analysis report",
              ].map((text) => (
                <li key={text} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-teal/10 text-teal">
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  {text}
                </li>
              ))}
            </ul>

            {/* Footer links */}
            <div className="mt-10 flex items-center justify-center gap-3 text-[11px] text-slate-400">
              <Link to="/terms" className="hover:text-slate-600">Terms of Service</Link>
              <span>·</span>
              <Link to="/privacy" className="hover:text-slate-600">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
